import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { BaseMap, Marker, RIDER_ICON, RESTAURANT_ICON, CUSTOMER_ICON, Polygon, Recenter, buildRiderIcon } from '@/components/MapBase';
import { Popup } from 'react-leaflet';
import { fmtDate, STATUS_LABELS, STATUS_COLORS } from '@/lib/format';
import { Badge } from '@/components/ui/badge';

export default function AdminLiveMap() {
  const [locations, setLocations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [zones, setZones] = useState([]);

  const load = async () => {
    try {
      const [l, o, s, z] = await Promise.all([
        api.get('/riders/locations'),
        api.get('/admin/orders?status=in_delivery'),
        api.get('/settings'),
        api.get('/zones'),
      ]);
      setLocations(l.data); setOrders(o.data); setSettings(s.data); setZones(z.data);
    } catch {}
  };

  useEffect(() => { load(); const iv = setInterval(load, 6000); return () => clearInterval(iv); }, []);

  // Center on the restaurant when settings arrive; before that we render at
  // a neutral coordinate that will be replaced by <Recenter/> on first load.
  const center = settings ? [settings.lat, settings.lng] : [37.5187, 15.0924];

  return (
    <AdminLayout testid="admin-live-map-page">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Mappa live</div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Rider e ordini in tempo reale</h1>
      </div>

      <div className="grid gap-4 lg:grid-cols-12">
        <div className="lg:col-span-8 h-[560px]">
          <BaseMap center={center} zoom={13} style={{ height: '100%' }}>
            <Recenter position={center} />
            {zones.map(z => (
              <Polygon key={z.id} positions={z.polygon.coordinates[0].map(([lng, lat]) => [lat, lng])}
                       pathOptions={{ color: z.color, weight: 2, fillOpacity: 0.1 }} />
            ))}
            {settings && <Marker position={[settings.lat, settings.lng]} icon={RESTAURANT_ICON}><Popup>{settings.name}</Popup></Marker>}
            {orders.map(o => (
              <Marker key={o.id} position={[o.delivery_lat, o.delivery_lng]} icon={CUSTOMER_ICON}>
                <Popup>
                  <div className="text-sm">
                    <div className="font-semibold">#{o.order_number}</div>
                    <div>{o.customer_name}</div>
                    <div className="text-xs opacity-70">{o.delivery_address}</div>
                  </div>
                </Popup>
              </Marker>
            ))}
            {locations.map(l => l.lat != null && (
              <Marker key={l.rider_id} position={[l.lat, l.lng]} icon={buildRiderIcon(l.rider?.name || 'Rider')}>
                <Popup>
                  <div className="text-sm">
                    <div className="font-semibold">{l.rider?.name || l.rider_id}</div>
                    <div className="text-xs opacity-70">Aggiornato {fmtDate(l.updated_at)}</div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </BaseMap>
        </div>

        <div className="lg:col-span-4 space-y-3">
          <div className="rounded-2xl bg-white border border-[--brand-border] p-4">
            <div className="font-display font-bold mb-3">Rider live ({locations.length})</div>
            {locations.length === 0 ? <div className="text-sm text-[--brand-muted]">Nessun rider connesso</div> :
              locations.map(l => (
                <div key={l.rider_id} className="border-t border-[--brand-border] pt-2 mt-2 first:border-0 first:pt-0 first:mt-0">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold text-sm">{l.rider?.name || '?'}</div>
                    {l.rider?.active && <Badge className="bg-[#EAF6F1] text-[#1F6F5B]">Attivo</Badge>}
                  </div>
                  {l.lat != null && <div className="text-xs text-[--brand-muted]">{l.lat.toFixed(4)}, {l.lng.toFixed(4)}</div>}
                  <div className="text-xs text-[--brand-muted]">Aggiornato {fmtDate(l.updated_at)}</div>
                </div>
              ))
            }
          </div>

          <div className="rounded-2xl bg-white border border-[--brand-border] p-4">
            <div className="font-display font-bold mb-3">In consegna ({orders.length})</div>
            {orders.length === 0 ? <div className="text-sm text-[--brand-muted]">Nessun ordine in consegna</div> :
              orders.map(o => (
                <div key={o.id} className="border-t border-[--brand-border] pt-2 mt-2 first:border-0 first:pt-0 first:mt-0">
                  <div className="flex justify-between items-center"><div className="font-semibold text-sm">#{o.order_number}</div><Badge className={`${STATUS_COLORS[o.status]}`}>{STATUS_LABELS[o.status]}</Badge></div>
                  <div className="text-xs text-[--brand-muted]">{o.customer_name} → {o.assigned_rider_name || 'non assegnato'}</div>
                </div>
              ))
            }
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
